import { useState, useEffect } from "react";
import { submitOpenHouseFeedbackInApp } from "../../utils/firebasefunctions";
import { formatTimeRange } from "../../utils/openHouseHelpers";
import { formatDate } from "../../utils/helper";
import { useStateValue } from "../../context/StateProvider";
import { toast } from "react-toastify";
import { feedbackGroups } from "../../utils/checkinCounts";

export default function FeedbackModal({ open, onClose, openHouse, onSaved }) {
  const [{ user }] = useStateValue();
  const [agentName, setAgentName] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [groups, setGroups] = useState("");
  const [secondShowings, setSecondShowings] = useState("");
  const [agentFeedback, setAgentFeedback] = useState("");
  const [buyerFeedback, setBuyerFeedback] = useState("");
  const [otherComments, setOtherComments] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !openHouse) return;
    const fb = openHouse.feedback;
    setAgentName(fb?.agentName ?? openHouse.hostingAgentName ?? "");
    setDate(fb?.date ?? openHouse.date ?? "");
    setTime(fb?.time ?? formatTimeRange(openHouse.startTime, openHouse.endTime));
    // Prefill from the check-in app's live count when no feedback exists yet.
    setGroups(fb?.groups ?? feedbackGroups(openHouse) ?? "");
    setSecondShowings(fb?.secondShowings ?? "");
    setAgentFeedback(fb?.agentFeedback ?? "");
    setBuyerFeedback(fb?.buyerFeedback ?? "");
    setOtherComments(fb?.otherComments ?? "");
  }, [open, openHouse]);

  if (!open || !openHouse) return null;

  const handleSubmit = async () => {
    if (!agentName || !date || !time || groups === "" || secondShowings === "" || !agentFeedback || !buyerFeedback) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSaving(true);
    try {
      const feedback = {
        agentName,
        date,
        time,
        groups: Number(groups),
        secondShowings: Number(secondShowings),
        agentFeedback,
        buyerFeedback,
        otherComments: otherComments || "",
      };
      await submitOpenHouseFeedbackInApp(openHouse.id, feedback, user.id);
      toast.success("Feedback saved. Composing recap…");
      onSaved && onSaved({ ...openHouse, feedback });
      onClose();
    } catch (err) {
      console.error("FeedbackModal error:", err);
      toast.error("Could not save feedback. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const labelClasses = "block text-xs font-semibold text-slate-700 mb-1";
  const inputClasses = "w-full bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-sm";

  return (
    <div className="fixed inset-0 bg-slate-900/55 z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-xl shadow-2xl flex flex-col max-h-[calc(100vh-32px)]">
        <div className="px-5 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Open House Feedback</h3>
          <p className="text-xs text-slate-500 mt-1">
            {openHouse.propertyAddress?.split(",")[0]} · {formatDate(openHouse.date)}
          </p>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-3">
          <div>
            <label className={labelClasses}>Agent First and Last Name *</label>
            <input value={agentName} onChange={(e) => setAgentName(e.target.value)} className={inputClasses} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClasses}>Date of Open House *</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClasses} />
            </div>
            <div>
              <label className={labelClasses}>Time of Open House *</label>
              <input
                type="text"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                placeholder="e.g. 1:00pm – 3:00pm"
                className={inputClasses}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClasses}>Number of Groups *</label>
              <input type="number" min="0" value={groups} onChange={(e) => setGroups(e.target.value)} className={inputClasses} />
            </div>
            <div>
              <label className={labelClasses}>Potential Second Showings *</label>
              <input type="number" min="0" value={secondShowings} onChange={(e) => setSecondShowings(e.target.value)} className={inputClasses} />
            </div>
          </div>

          <div>
            <label className={labelClasses}>Agent's Favorable / Unfavorable Feedback *</label>
            <textarea value={agentFeedback} onChange={(e) => setAgentFeedback(e.target.value)} rows={3} className={inputClasses} />
          </div>

          <div>
            <label className={labelClasses}>Buyers' Favorable / Unfavorable Feedback *</label>
            <textarea value={buyerFeedback} onChange={(e) => setBuyerFeedback(e.target.value)} rows={3} className={inputClasses} />
          </div>

          <div>
            <label className={labelClasses}>Any Other Comments</label>
            <textarea value={otherComments} onChange={(e) => setOtherComments(e.target.value)} rows={2} className={inputClasses} />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-md">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold text-white bg-slate-800 hover:bg-slate-900 rounded-md disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save Feedback"}
          </button>
        </div>
      </div>
    </div>
  );
}
